import {
  API_BASE_URL,
  API_ROOT_URL,
  BACKEND_URL,
  CASCADE_TRACING_API_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AdminAttachmentTemplateDetail,
  AdminAttachmentTemplatesResponse,
  AiBulkCreatePayload,
  AiDatasetGenerateResponse,
  AiDatasetType,
  AttachmentTemplateOption,
  AttachmentTemplatesResponse,
  BulkDocumentsUpdatePayload,
  BulkExportResult,
  BulkStatusPayload,
  BuyerCompaniesResponse,
  CheckAttachmentTemplatesResponse,
  CheckOrderNumbersResponse,
  CompareCandidatesPayload,
  CompareCandidatesResponse,
  CreateAdminTemplatePayload,
  CreateOrderTypeResponse,
  CreatePurchaseOrderPayload,
  CreateSalesOrderPayload,
  DetachOrderPayload,
  EntityScreeningMatch,
  ExportPayload,
  ExportPurchaseOrdersPayload,
  InternalOrderListResponse,
  LinkSalesOrderPayload,
  OrderParticipant,
  OrderStyleOption,
  OrderTag,
  OrderTagInUse,
  OrderTypeListResponse,
  PatchInternalOrderPayload,
  PatchPurchaseOrderPayload,
  PatchPurchaseOrderResponse,
  PatchSalesOrderPayload,
  PatchSalesOrderResponse,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderListResponse,
  RefreshCalculationPayload,
  RemoveNodeDocumentPayload,
  SalesOrder,
  SalesOrderListResponse,
  SupplierCompaniesResponse,
  ToggleArchivePayload,
  TogglePurchaseOrderArchivePayload,
  TraceGraphMeta,
  TraceGraphResponse,
  TracingNodeDetail,
  TransactionLink,
  UpdateAdminTemplatePayload,
  ValidateAiSummaryPayload,
} from './types';

/** Fetch a single tracing node (with its documents) by id. `qs` carries orderId + type. */
export async function fetchTracingNode(
  nodeId: string,
  qs: string,
): Promise<TracingNodeDetail> {
  const res = await apiFetch<{ data: TracingNodeDetail }>(
    `/cascade-tracing/transactions/node/${encodeURIComponent(nodeId)}?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

/** Remove a document attached to a tracing node. */
export async function removeNodeDocument(
  payload: RemoveNodeDocumentPayload,
): Promise<void> {
  await apiFetch<void>(
    '/cascade-tracing/transactions/remove-node-document',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchSalesOrders(
  body: Record<string, unknown>,
): Promise<SalesOrderListResponse> {
  return apiFetch<SalesOrderListResponse>(
    '/cascade-tracing/orders/sales/list',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

/** Styles referenced on sales orders — for the products list filter. */
export async function fetchSalesOrderStyles(): Promise<{
  data: OrderStyleOption[];
}> {
  return apiFetch<{ data: OrderStyleOption[] }>(
    '/cascade-tracing/orders/sales/styles',
    {},
    CASCADE_TRACING_API_URL,
  );
}

/** Styles referenced on purchase orders (and internal orders) — for the products list filter. */
export async function fetchPurchaseOrderStyles(): Promise<{
  data: OrderStyleOption[];
}> {
  return apiFetch<{ data: OrderStyleOption[] }>(
    '/cascade-tracing/orders/purchases/styles',
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function patchSalesOrder(
  id: string,
  body: PatchSalesOrderPayload,
): Promise<PatchSalesOrderResponse> {
  return apiFetch<PatchSalesOrderResponse>(
    `/cascade-tracing/orders/sales/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function toggleArchiveSalesOrders(
  payload: ToggleArchivePayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/toggle-archive',
    { method: 'PUT', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function bulkPatchSalesOrders(
  payload: BulkStatusPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/sales/bulk-patch',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function exportSalesOrders(
  payload: ExportPayload,
): Promise<BulkExportResult> {
  const res = await apiFetch<{ data: BulkExportResult }>(
    '/cascade-tracing/orders/trace/sales/bulk-export',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

export async function createAiSummaryBulk(
  payload: AiBulkCreatePayload,
): Promise<{ data: { queued: number } }> {
  return apiFetch<{ data: { queued: number } }>(
    '/tracing-ai/bulk-summary',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function fetchBuyerCompanies(
  qs: string,
): Promise<BuyerCompaniesResponse> {
  return apiFetch<BuyerCompaniesResponse>(
    `/api/companies?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAiSummaryById(
  summaryId: string,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    `/tracing-ai/summary/${summaryId}`,
    {},
    BACKEND_URL,
  );
}

/**
 * Generates (or regenerates) the dataset CSV for one analysis type and returns
 * a signed download URL valid for 1 hour. Backend rejects non-Retraced users
 * with a 403.
 */
export async function generateAiSummaryDataset(
  summaryId: string,
  type: AiDatasetType,
): Promise<{ data: AiDatasetGenerateResponse }> {
  return apiFetch<{ data: AiDatasetGenerateResponse }>(
    `/tracing-ai/${summaryId}/dataset?type=${type}`,
    {},
    BACKEND_URL,
  );
}

export async function validateAiSummary(
  summaryId: string,
  payload: ValidateAiSummaryPayload,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    `/tracing-ai/summary/${summaryId}/validate`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

/** Takes a pre-built query string (param encoding belongs in the hook). */
export async function fetchAttachmentTemplatesForImport(
  qs: string,
): Promise<{ data: AttachmentTemplateOption[] }> {
  return apiFetch<{ data: AttachmentTemplateOption[] }>(
    `/api/attachment-templates/aggregate?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function createSalesOrder(
  body: CreateSalesOrderPayload,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    '/cascade-tracing/orders/sales',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function refreshSalesOrderCalculations(
  payload: RefreshCalculationPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/bulk-refresh-calculation',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

/** Takes a pre-built query string (param encoding belongs in the hook). */
export async function fetchAttachmentTemplates(
  qs: string,
): Promise<AttachmentTemplatesResponse> {
  return apiFetch<AttachmentTemplatesResponse>(
    `/api/attachment-templates/documents?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/** Internal orders are purchase orders, so their documents go through this route too. */
export async function bulkUpdatePurchaseOrderDocuments(
  payload: BulkDocumentsUpdatePayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/purchases/bulk-documents-update',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function bulkUpdateSalesOrderDocuments(
  payload: BulkDocumentsUpdatePayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/sales/bulk-documents-update',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function linkSalesOrder(
  payload: LinkSalesOrderPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/link-sales-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function unlinkSalesOrder(payload: {
  purchaseOrderId: string;
  salesOrderId: string;
}): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/unlink-sales-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

// ─── Purchase Orders ──────────────────────────────────────────────────────────

export async function fetchPurchaseOrders(
  body: Record<string, unknown>,
): Promise<PurchaseOrderListResponse> {
  return apiFetch<PurchaseOrderListResponse>(
    '/cascade-tracing/orders/purchases/list',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchCompareCandidates(
  body: CompareCandidatesPayload,
): Promise<CompareCandidatesResponse> {
  return apiFetch<CompareCandidatesResponse>(
    '/cascade-tracing/orders/purchases/compare-candidates',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchPurchaseOrdersByNode(params: {
  nodeId: string;
  supplierCompanyId: string;
  orderId: string;
  type: string;
  isArchived?: boolean;
}): Promise<PurchaseOrderListResponse> {
  const qs = new URLSearchParams(
    Object.entries({
      ...params,
      isArchived: String(params.isArchived ?? false),
    })
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return apiFetch<PurchaseOrderListResponse>(
    `/cascade-tracing/orders/purchases/by-node?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function patchPurchaseOrder(
  id: string,
  body: PatchPurchaseOrderPayload,
): Promise<PatchPurchaseOrderResponse> {
  return apiFetch<PatchPurchaseOrderResponse>(
    `/cascade-tracing/orders/purchases/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

/** Accepts a fully-formed PUT body. Body assembly belongs in the hook. */
export async function updatePurchaseOrder(
  id: string,
  body: {
    orderNumber: string;
    attachmentTemplateId: string;
    buyer: { id: string; facilityProcesses: string[] };
    supplier: { id: string; facilityProcesses: string[] };
    lines?: Array<{
      id?: string;
      styleId: string;
      quantity?: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string;
    }>;
    status?: string;
    orderTypeId?: string | null;
    orderTags?: string[];
    orderDate?: string | null;
    deliveryDate?: string | null;
    agreedDeliveryDate?: string | null;
    receiver?: { id: string; facilityProcesses: string[] } | null;
    responsibleUserIds?: string[] | null;
    attachments?: Array<{ fileId?: string; documentId?: string; type: string }>;
  },
): Promise<{ data: PurchaseOrder }> {
  return apiFetch<{ data: PurchaseOrder }>(
    `/cascade-tracing/orders/purchases/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function toggleArchivePurchaseOrders(
  payload: TogglePurchaseOrderArchivePayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/toggle-archive',
    { method: 'PUT', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function bulkPatchPurchaseOrders(
  payload: BulkStatusPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/purchases/bulk-patch',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function exportPurchaseOrders(
  payload: ExportPurchaseOrdersPayload,
): Promise<BulkExportResult> {
  const res = await apiFetch<{ data: BulkExportResult }>(
    '/cascade-tracing/orders/trace/purchase/bulk-export',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

export async function fetchSupplierCompanies(
  qs: string,
): Promise<SupplierCompaniesResponse> {
  return apiFetch<SupplierCompaniesResponse>(
    `/api/companies?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/** Fetch sales orders for the link-SO sheet (non-archived only). */
export async function fetchSalesOrdersForLink(qs: string): Promise<{
  data: SalesOrder[];
  meta: { totalCount: number };
}> {
  return apiFetch<{
    data: SalesOrder[];
    meta: { totalCount: number };
  }>(`/v3/cascade-tracing/orders/sales?${qs}`, {});
}

export async function linkPurchaseOrderToSalesOrder(
  payload: LinkSalesOrderPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/link-sales-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function unlinkPurchaseOrderFromSalesOrder(payload: {
  purchaseOrderId: string;
  salesOrderId: string;
}): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/unlink-sales-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function createPurchaseOrder(
  body: CreatePurchaseOrderPayload,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    '/cascade-tracing/orders/purchases',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns raw response — the hook unwraps `.data`. */
export async function checkPurchaseOrderNumbers(
  orderNumbers: string[],
): Promise<CheckOrderNumbersResponse> {
  return apiFetch<CheckOrderNumbersResponse>(
    '/cascade-tracing/orders/purchases/check-purchase-order-numbers',
    { method: 'POST', body: JSON.stringify(orderNumbers) },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns raw response — the hook unwraps `.data`. */
export async function checkSalesOrderNumbers(
  orderNumbers: string[],
): Promise<{ data: string[] }> {
  return apiFetch<{ data: string[] }>(
    '/cascade-tracing/orders/sales/check-sales-order-numbers',
    { method: 'POST', body: JSON.stringify(orderNumbers) },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns companies that participated in selected orders. */
export async function fetchParticipantsByOrderIds(body: {
  orderIds: string[];
  hasParticipantConnection: boolean;
}): Promise<{ data: OrderParticipant[] }> {
  return apiFetch<{ data: OrderParticipant[] }>(
    '/cascade-tracing/orders/purchases/participants',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

// ─── Internal Orders ──────────────────────────────────────────────────────────

export async function fetchInternalOrders(
  body: Record<string, unknown>,
): Promise<InternalOrderListResponse> {
  return apiFetch<InternalOrderListResponse>(
    '/cascade-tracing/orders/internal/list',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function patchInternalOrder(
  id: string,
  body: PatchInternalOrderPayload,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    `/v3/cascade-tracing/orders/internal/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

/** Internal orders are purchase orders flagged `is_internal`, so they patch through the purchase route. */
export async function bulkPatchInternalOrders(
  payload: BulkStatusPayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/purchases/bulk-patch',
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function toggleArchiveInternalOrders(
  payload: TogglePurchaseOrderArchivePayload,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/toggle-archive',
    { method: 'PUT', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

/** Internal orders are purchase orders flagged `is_internal`, so they export through the purchase route. */
export async function exportInternalOrders(
  payload: ExportPurchaseOrdersPayload,
): Promise<BulkExportResult> {
  return exportPurchaseOrders(payload);
}

/**
 * Checks whether the given PO/SO pairs have compatible (same) attachment
 * templates. Returns a list of pairs that do NOT share the same template.
 * An empty list means all pairs are compatible.
 */
export async function checkSimilarTracingTemplates(
  pairs: Array<{
    purchaseOrderNumber: string;
    salesOrderNumber: string;
    index: number;
  }>,
): Promise<CheckAttachmentTemplatesResponse> {
  return apiFetch<CheckAttachmentTemplatesResponse>(
    '/cascade-tracing/orders/check-attachment-templates',
    { method: 'POST', body: JSON.stringify(pairs) },
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Convert an order line's primary quantity to kilograms for a style. The
 * backend resolves `productId` against the styles table (product_level 'style'),
 * so a style id is passed here.
 */
export async function calculateProductKg(body: {
  productId: string;
  unit: string;
  quantity: number;
}): Promise<{ data: { quantity: number; unit?: string } }> {
  return apiFetch<{ data: { quantity: number; unit?: string } }>(
    '/cascade-tracing/orders/calculations/product-kg',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

/** The company's whole tag catalogue, newest first; the hook unwraps `.data`. */
export async function fetchOrderTags(): Promise<{ data: OrderTag[] }> {
  return apiFetch<{ data: OrderTag[] }>(
    '/cascade-tracing/orders/tags/catalogue',
    {},
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Tags in use on the company's orders, scoped to one list. Deliberately not
 * `fetchOrderTags`: that returns the `tracing_order_tags` catalogue, which the
 * create/edit pickers need for its ids but which drifts from what is actually
 * on the orders (CHARLIE-2696).
 */
export async function fetchOrderTagsInUse(
  scope: 'purchases' | 'sales' | 'internal',
): Promise<OrderTagInUse[]> {
  const res = await apiFetch<{ data: OrderTagInUse[] }>(
    `/cascade-tracing/orders/tags?scope=${scope}`,
    {},
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

export async function fetchOrderTypes(): Promise<OrderTypeListResponse> {
  return apiFetch<OrderTypeListResponse>(
    '/cascade-tracing/orders/order-types',
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function createOrderType(
  orderType: string,
): Promise<CreateOrderTypeResponse> {
  return apiFetch<CreateOrderTypeResponse>(
    '/cascade-tracing/orders/order-types',
    { method: 'POST', body: JSON.stringify({ orderType }) },
    CASCADE_TRACING_API_URL,
  );
}

/** Answers the created (or already existing) tag under `data`. */
export async function createOrderTag(
  orderTag: string,
): Promise<{ data: OrderTag }> {
  return apiFetch<{ data: OrderTag }>(
    '/cascade-tracing/orders/tags',
    { method: 'POST', body: JSON.stringify({ orderTag }) },
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Takes a pre-built query string (param encoding belongs in the hook).
 * Returns the raw response — response normalisation belongs in the hook.
 */
export async function fetchAttachmentTemplateRules(
  templateId: string,
  qs: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/api/attachment-templates/${templateId}/documents?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export interface AttachOrderPayload {
  nodeId: string;
  meta?: Record<string, unknown>;
  orderNumber: string;
  buyer?: { id: string; facilityProcesses: string[] };
  supplier?: { id: string; facilityProcesses: string[] };
  receiver?: { id: string; facilityProcesses: string[] } | null;
  lines?: Array<{ styleId?: string; quantity?: number; unit?: string }>;
  orderTypeId?: string | null;
  orderDate?: string | null;
  agreedDeliveryDate?: string | null;
  orderTags?: string[];
  attachmentTemplateId?: string | null;
  attachments?: Array<{ fileId?: string; documentId?: string; type: string }>;
  responsibleUserIds?: string[] | null;
}

export async function attachOrderToNode(
  payload: AttachOrderPayload,
): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    '/cascade-tracing/transactions/attach-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function attachExistingOrderToNode(payload: {
  nodeId: string;
  purchaseOrderId: string;
  meta?: Record<string, unknown>;
}): Promise<{ data: Record<string, unknown> }> {
  return apiFetch<{ data: Record<string, unknown> }>(
    '/cascade-tracing/transactions/attach-existing-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

/** Accepts a fully-formed PUT body. Body assembly and response unwrapping belong in the hook. */
export async function updateSalesOrder(
  id: string,
  body: {
    orderNumber: string;
    attachmentTemplateId: string;
    buyer: { id: string; facilityProcesses: string[] };
    supplier: { id: string; facilityProcesses: string[] };
    lines?: Array<{
      id?: string;
      styleId: string;
      quantity?: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string;
    }>;
    status?: string;
    orderTypeId?: string | null;
    orderDate?: string | null;
    agreedDeliveryDate?: string | null;
    deliveryDate?: string | null;
    orderTags?: string[];
    receiver?: { id: string; facilityProcesses: string[] } | null;
    responsibleUserIds?: string[] | null;
    attachments?: Array<{ fileId?: string; documentId?: string; type: string }>;
  },
): Promise<{ data: SalesOrder }> {
  return apiFetch<{ data: SalesOrder }>(
    `/cascade-tracing/orders/sales/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function verifyDocument(tracingDocumentId: string): Promise<void> {
  return apiFetch<void>(
    `/cascade-tracing/transactions/verify-document/${encodeURIComponent(tracingDocumentId)}`,
    { method: 'POST' },
    CASCADE_TRACING_API_URL,
  );
}

export async function unverifyDocument(
  tracingDocumentId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cascade-tracing/transactions/unverify-document/${encodeURIComponent(tracingDocumentId)}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

export async function attachDocumentsToLink(
  linkId: string,
  documents: Array<{ fileId: string; documentId?: string; type: string }>,
): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/transactions/attach-link-documents',
    { method: 'POST', body: JSON.stringify({ linkId, documents }) },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function fetchSalesOrderById(
  id: string,
): Promise<{ data: SalesOrder }> {
  return apiFetch<{ data: SalesOrder }>(
    `/cascade-tracing/orders/sales/${id}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function deleteSalesOrder(id: string): Promise<void> {
  return apiFetch<void>(
    `/cascade-tracing/orders/sales/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

export async function deletePurchaseOrder(id: string): Promise<void> {
  return apiFetch<void>(
    `/cascade-tracing/orders/purchases/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Internal orders are purchase orders whose buyer and supplier are the same
 * company — every internal-order endpoint targets the purchases resource, and
 * delete is no exception.
 */
export async function deleteInternalOrder(id: string): Promise<void> {
  return deletePurchaseOrder(id);
}

export async function linkOrderLineProduct(body: {
  productId: string;
  meta: { orderLineId: string; type: 'SALES_ORDER' | 'PURCHASE_ORDER' };
}): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/orders/order-lines/product-linking',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function fetchPurchaseOrderById(
  id: string,
): Promise<{ data: PurchaseOrderDetail }> {
  return apiFetch<{ data: PurchaseOrderDetail }>(
    `/cascade-tracing/orders/purchases/${id}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function fetchTraceGraphBySalesOrderId(
  soId: string,
): Promise<{ data: TraceGraphResponse }> {
  return apiFetch<{ data: TraceGraphResponse }>(
    `/cascade-tracing/orders/trace/sales/${soId}`,
    { method: 'GET' },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchProductTraceBySalesOrderId(
  soId: string,
): Promise<import('./types').ProductTraceResponse> {
  return apiFetch(
    `/v3/cascade-tracing/orders/product-trace/sales/${soId}`,
    { method: 'GET' },
    API_BASE_URL,
  );
}

export async function fetchProductTraceByPurchaseOrderId(
  poId: string,
): Promise<import('./types').ProductTraceResponse> {
  return apiFetch(
    `/v3/cascade-tracing/orders/product-trace/purchases/${poId}`,
    { method: 'GET' },
    API_BASE_URL,
  );
}

export async function fetchTraceGraphByPurchaseOrderId(
  poId: string,
): Promise<{ data: TraceGraphResponse }> {
  return apiFetch<{ data: TraceGraphResponse }>(
    `/cascade-tracing/orders/trace/purchases/${poId}`,
    { method: 'GET' },
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Takes a pre-built query string (param encoding and pagination defaults belong in the hook).
 * Returns the raw server envelope — response unwrapping belongs in the hook.
 */
export async function fetchEntityScreeningMatches(
  qs: string,
): Promise<{ data: EntityScreeningMatch[] }> {
  return apiFetch<{ data: EntityScreeningMatch[] }>(
    `/entity-screening/companies?${qs}`,
    { method: 'GET' },
    BACKEND_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function fetchTransactionLinkById(
  linkId: string,
): Promise<{ data: TransactionLink }> {
  return apiFetch<{ data: TransactionLink }>(
    `/cascade-tracing/transactions/link/${linkId}`,
    { method: 'GET' },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function detachOrder(
  payload: DetachOrderPayload,
): Promise<{ data: TraceGraphResponse }> {
  return apiFetch<{ data: TraceGraphResponse }>(
    '/cascade-tracing/transactions/detach-existing-order',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

/** Returns the raw server envelope. Response unwrapping belongs in the hook. */
export async function deleteOriginOrder(
  transactionId: string,
  meta: TraceGraphMeta,
): Promise<{ data: TraceGraphResponse }> {
  return apiFetch<{ data: TraceGraphResponse }>(
    `/cascade-tracing/transactions/origin/${encodeURIComponent(transactionId)}`,
    { method: 'DELETE', body: JSON.stringify({ meta }) },
    CASCADE_TRACING_API_URL,
  );
}

export async function removeLinkDocument(payload: {
  linkId: string;
  documentId: string;
}): Promise<void> {
  return apiFetch<void>(
    '/v3/cascade-tracing/transactions/remove-link-document',
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function createProductTransformation(payload: {
  nodeOrderlineId: string;
  orderId: string;
  type: string;
  inputStyleId: string;
  inputQuantity: number;
  inputUnit: string;
  outputStyleId: string;
  outputQuantity: number;
  outputUnit: string;
}): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/transactions/product-transformation',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function attachOriginToNode(payload: {
  meta?: Record<string, unknown>;
  nodeId: string;
  facilityProcesses: string[];
  countryCode?: string;
  lines: Array<{
    styleId: string;
    unit: string;
    quantity?: number;
    secondaryUnit?: string;
    secondaryQuantity?: number;
  }>;
  attachmentTemplateId?: string;
  documents: Array<{ fileId?: string; documentId?: string; type: string }>;
}): Promise<void> {
  return apiFetch<void>(
    '/cascade-tracing/transactions/attach-origin',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchInternalOrderById(
  id: string,
): Promise<{ data: import('./types').InternalOrderDetail }> {
  return apiFetch<{ data: import('./types').InternalOrderDetail }>(
    `/cascade-tracing/orders/purchases/${id}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchInternalOrderAttachments(
  ioId: string,
): Promise<{ data: import('./types').SalesOrderAttachmentsResponse }> {
  return apiFetch<{ data: import('./types').SalesOrderAttachmentsResponse }>(
    `/cascade-tracing/orders/purchases/${ioId}/attachments`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchTraceGraphByInternalOrderId(
  ioId: string,
): Promise<{ data: TraceGraphResponse }> {
  return apiFetch<{ data: TraceGraphResponse }>(
    `/cascade-tracing/orders/trace/purchases/${ioId}`,
    { method: 'GET' },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchProductTraceByInternalOrderId(
  ioId: string,
): Promise<import('./types').ProductTraceResponse> {
  return apiFetch(
    `/v3/cascade-tracing/orders/product-trace/purchases/${ioId}`,
    { method: 'GET' },
    API_BASE_URL,
  );
}

export async function fetchProductLineLink(
  linkId: string,
  orderId: string,
  type: string,
): Promise<import('./types').ProductLineLinkResponse> {
  const qs = new URLSearchParams({ orderId, type }).toString();
  return apiFetch(
    `/v3/cascade-tracing/transactions/product-line-link/${linkId}?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchSalesOrderAttachments(
  soId: string,
): Promise<{ data: import('./types').SalesOrderAttachmentsResponse }> {
  return apiFetch<{ data: import('./types').SalesOrderAttachmentsResponse }>(
    `/cascade-tracing/orders/sales/${soId}/attachments`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchPurchaseOrderAttachments(
  poId: string,
): Promise<{ data: import('./types').SalesOrderAttachmentsResponse }> {
  return apiFetch<{ data: import('./types').SalesOrderAttachmentsResponse }>(
    `/cascade-tracing/orders/purchases/${poId}/attachments`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function downloadLinkDocuments(linkId: string): Promise<void> {
  return apiFetch<void>(
    `/cascade-tracing/transactions/download-link-documents/${encodeURIComponent(linkId)}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

/** Queues a zip of an order's trace documents; the caller is emailed the link. */
export async function downloadOrderDocuments(
  orderId: string,
  type: string,
): Promise<void> {
  await apiFetch<void>(
    `/cascade-tracing/transactions/download-order-documents/${encodeURIComponent(orderId)}?type=${encodeURIComponent(type)}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

// ─── Attachment Templates ─────────────────────────────────────────────────────

export async function fetchAdminAttachmentTemplates(
  qs: string,
): Promise<AdminAttachmentTemplatesResponse> {
  return apiFetch<AdminAttachmentTemplatesResponse>(
    `/attachment-templates/aggregate?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function setAttachmentTemplatePreference(
  attachmentTemplateId: string | null,
): Promise<void> {
  await apiFetch<unknown>(
    '/attachment-templates/preference',
    { method: 'PUT', body: JSON.stringify({ attachmentTemplateId }) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchAdminAttachmentTemplate(
  id: string,
): Promise<AdminAttachmentTemplateDetail> {
  const res = await apiFetch<{ data: AdminAttachmentTemplateDetail }>(
    `/attachment-templates/${id}/details`,
    {},
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

export async function createAdminAttachmentTemplate(
  body: CreateAdminTemplatePayload,
): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>(
    '/attachment-templates',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
  return res.data;
}

export async function updateAdminAttachmentTemplate(
  id: string,
  body: UpdateAdminTemplatePayload,
): Promise<void> {
  await apiFetch<unknown>(
    `/attachment-templates/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function archiveAdminAttachmentTemplate(
  id: string,
): Promise<void> {
  await apiFetch<unknown>(
    `/attachment-templates/${id}/archive`,
    { method: 'POST' },
    CASCADE_TRACING_API_URL,
  );
}
